import type { Coord3, RouteFeature, RouteInstruction } from '../types';

// Inferred product threshold, deliberately explicit and covered at both sides by tests.
export const ROUTE_CORRIDOR_THRESHOLD_M = 75;

interface InstructionSelection {
  instruction: RouteInstruction;
  instructionIndex: number;
}

export type ManeuverState =
  | ({ status: 'first'; progressM: null; distanceToInstructionM: null } & InstructionSelection)
  | ({ status: 'next'; progressM: number; distanceToInstructionM: number } & InstructionSelection)
  | ({ status: 'finished'; progressM: number; distanceToInstructionM: 0 } & InstructionSelection)
  | {
      status: 'off_route';
      progressM: number | null;
      nearestDistanceM: number;
      distanceToInstructionM: null;
      instruction: null;
      instructionIndex: null;
    }
  | {
      status: 'unavailable';
      progressM: null;
      distanceToInstructionM: null;
      instruction: null;
      instructionIndex: null;
    };

interface Projection {
  progressM: number;
  nearestDistanceM: number;
}

interface ValidInstruction extends InstructionSelection {
  startIndex: number;
}

const EARTH_RADIUS_M = 6_371_000;
const TO_RADIANS = Math.PI / 180;

function isValidCoordinate(coordinate: Coord3): boolean {
  return Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1]);
}

function haversineM(a: Coord3, b: Coord3): number {
  const lat1 = a[1] * TO_RADIANS;
  const lat2 = b[1] * TO_RADIANS;
  const deltaLat = (b[1] - a[1]) * TO_RADIANS;
  const deltaLon = (b[0] - a[0]) * TO_RADIANS;
  const sinLat = Math.sin(deltaLat / 2);
  const sinLon = Math.sin(deltaLon / 2);
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLon * sinLon;
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function routeDistances(coordinates: Coord3[]): number[] {
  const cumulative = [0];
  for (let index = 1; index < coordinates.length; index += 1) {
    cumulative.push(cumulative[index - 1] + haversineM(coordinates[index - 1], coordinates[index]));
  }
  return cumulative;
}

function projectOntoRoute(
  coordinates: Coord3[],
  location: Coord3,
  cumulative: number[]
): Projection {
  const latitudeRadians = location[1] * TO_RADIANS;
  const xScale = EARTH_RADIUS_M * Math.cos(latitudeRadians) * TO_RADIANS;
  const yScale = EARTH_RADIUS_M * TO_RADIANS;
  let nearestDistanceM = Number.POSITIVE_INFINITY;
  let progressM = 0;

  for (let index = 0; index < coordinates.length - 1; index += 1) {
    const startX = (coordinates[index][0] - location[0]) * xScale;
    const startY = (coordinates[index][1] - location[1]) * yScale;
    const endX = (coordinates[index + 1][0] - location[0]) * xScale;
    const endY = (coordinates[index + 1][1] - location[1]) * yScale;
    const segmentX = endX - startX;
    const segmentY = endY - startY;
    const segmentSquared = segmentX * segmentX + segmentY * segmentY;
    const fraction = segmentSquared === 0
      ? 0
      : Math.max(0, Math.min(1, -(startX * segmentX + startY * segmentY) / segmentSquared));
    const projectedX = startX + fraction * segmentX;
    const projectedY = startY + fraction * segmentY;
    const distanceM = Math.hypot(projectedX, projectedY);

    if (distanceM < nearestDistanceM) {
      nearestDistanceM = distanceM;
      progressM = cumulative[index] + fraction * (cumulative[index + 1] - cumulative[index]);
    }
  }

  return { progressM, nearestDistanceM };
}

function validInstructionStart(
  instruction: RouteInstruction,
  coordinateCount: number
): number | null {
  const start = instruction.interval[0];
  const end = instruction.interval[1];
  return Number.isInteger(start)
    && Number.isInteger(end)
    && start >= 0
    && end >= start
    && end < coordinateCount
    ? start
    : null;
}

function findFinishInstructionIndex(instructions: RouteInstruction[]): number {
  for (let index = instructions.length - 1; index >= 0; index -= 1) {
    if (instructions[index].sign === 4) return index;
  }
  return -1;
}

function unavailableState(): ManeuverState {
  return {
    status: 'unavailable',
    progressM: null,
    distanceToInstructionM: null,
    instruction: null,
    instructionIndex: null
  };
}

export function resolveNextInstruction(
  route: RouteFeature,
  instructions: RouteInstruction[],
  location: Coord3 | null,
  previousProgress: number | null
): ManeuverState {
  const coordinates = route.geometry.coordinates;
  if (
    instructions.length === 0
    || coordinates.length < 2
    || coordinates.some((coordinate) => !isValidCoordinate(coordinate))
  ) {
    return unavailableState();
  }

  const cumulative = routeDistances(coordinates);
  const totalDistanceM = cumulative[cumulative.length - 1];
  if (totalDistanceM <= 0 || (location !== null && !isValidCoordinate(location))) {
    return unavailableState();
  }

  const validInstructions = instructions.flatMap<ValidInstruction>((instruction, instructionIndex) => {
    const startIndex = validInstructionStart(instruction, coordinates.length);
    return startIndex === null ? [] : [{ instruction, instructionIndex, startIndex }];
  });
  if (validInstructions.length === 0) return unavailableState();

  if (!location) {
    return {
      status: 'first',
      instruction: validInstructions[0].instruction,
      instructionIndex: validInstructions[0].instructionIndex,
      progressM: null,
      distanceToInstructionM: null
    };
  }

  const projected = projectOntoRoute(coordinates, location, cumulative);
  const previous = Number.isFinite(previousProgress) ? previousProgress as number : 0;
  const progressM = Math.min(totalDistanceM, Math.max(0, previous, projected.progressM));

  if (projected.nearestDistanceM > ROUTE_CORRIDOR_THRESHOLD_M) {
    return {
      status: 'off_route',
      progressM: previousProgress === null ? null : progressM,
      nearestDistanceM: projected.nearestDistanceM,
      distanceToInstructionM: null,
      instruction: null,
      instructionIndex: null
    };
  }

  const validInstructionValues = validInstructions.map(({ instruction }) => instruction);
  const validFinishIndex = findFinishInstructionIndex(validInstructionValues);
  if (progressM >= totalDistanceM - 0.5) {
    const selection = validFinishIndex >= 0
      ? validInstructions[validFinishIndex]
      : validInstructions[validInstructions.length - 1];
    return {
      status: 'finished',
      instruction: selection.instruction,
      instructionIndex: selection.instructionIndex,
      progressM: totalDistanceM,
      distanceToInstructionM: 0
    };
  }

  for (const selection of validInstructions) {
    if (cumulative[selection.startIndex] >= progressM - 0.5) {
      return {
        status: 'next',
        instruction: selection.instruction,
        instructionIndex: selection.instructionIndex,
        progressM,
        distanceToInstructionM: Math.max(0, cumulative[selection.startIndex] - progressM)
      };
    }
  }

  return unavailableState();
}
